import { $t } from '@/i18n';
import { set, forEach, get } from 'lodash';
import { message, Modal, notification } from 'ant-design-vue';
import {
  XyModalSelectorMeta, XyPageletPropsEditorMeta, XyGlobalModalSettingMeta,
} from '../paramsFormComponents';
import { stringInterop, vueDeepSet } from '@tencent/ui-core/lib/utils';
import { toPathParts } from '@tencent/data-schema-core/lib/utils/path';
import { toString } from '@/utils/toString';
import { CATEGORY } from './const';
import pageLoader from '@loaders/page/loader';

// 关闭弹窗
export const getXyHideModalAtom = type => ({
  id: 'xy:hideModal',
  name: $t('k_87bb5e80'),
  category: CATEGORY.UI,
  visible: type === 'modal',
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'data',
        type: 'array',
        title: $t('k_afd3dc81'),
        'uc:allowInterop': true,
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            keyIsInteropable: true,
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string' },
            { id: 'value', type: 'string' },
          ],
        },
      },
    ],
  },
  paramsFormComponents: [
    XyPageletPropsEditorMeta,
  ],
  async execute(ctx, params) {
    const { wContext, w } = ctx.renderer;  // xy 上下文
    const { data } = params;
    const hideData = {};
    if (Array.isArray(data)) {
      data.forEach((item) => {
        const { key, value } = item;
        hideData[key] = value;
      });
    }

    if (wContext?.type === 'globalModal') {
      await w.hideGlobalModal(wContext.id, hideData);
    } else {
      await wContext.hideModal(null, hideData);
    }
  },
});
// 弹出消息窗口
export const alertAtom = {
  id: 'uicore:alert',
  name: $t('k_f94021f4'),
  category: $t('k_9a34c9f8'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'text',
        type: 'string',
        title: $t('k_befd930'),
        default: $t('k_bbd635d2'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'icon',
        type: 'string',
        title: $t('k_c72881c6'),
        enum: [
          { value: '', label: $t('k_42b5bcb3') },
          { value: 'info' },
          { value: 'success' },
          { value: 'error' },
          { value: 'warning' },
        ],
      },
      {
        id: 'width',
        type: 'string',
        title: $t('k_685d6a2f'),
        description: $t('k_c1edc7b3'),
      },
    ],
  },
  execute: async (ctx, params) => {
    const text = toString(params.text).trim();
    if (!text) throw new Error($t('k_bbd635d2'));

    return new Promise((resolve) => {
      const modalOption = {
        content: h => h('div', { style: { maxWidth: '80vw', whiteSpace: 'pre-wrap' } }, [text]),
        maskClosable: true,
        onOk: () => void resolve(),
        onCancel: () => void resolve(),
      };
      if (params.width) set(modalOption, 'width', params.width);
      // 无图标则渲染空的 VNode, 否则使用 Modal 方法对应的图标
      if (!params.icon) modalOption.icon = h => h();
      Modal[params.icon || 'info'](modalOption);
    });
  },
};
// 弹出确认框
export const confirmAtom = {
  id: 'uicore:confirm',
  name: $t('k_6c21d053'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'text',
        type: 'string',
        title: $t('k_ce9aec82'),
        default: $t('k_5f65a3f5'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'abortIfRejected',
        type: 'boolean',
        title: $t('k_a6c10c8b'),
        default: true,
      },
      {
        id: 'okText',
        type: 'string',
        title: $t('k_3e4288c8'),
        default: $t('k_a5064e7f'),
      },
      {
        id: 'cancelText',
        type: 'string',
        title: $t('k_4bbade06'),
        default: $t('k_83ed1744'),
      },
      {
        id: 'icon',
        title: $t('k_c72881c6'),
        type: 'string',
        enum: [
          { value: 'question' },
          { value: 'question-circle' },
          { value: 'info' },
          { value: 'info-circle' },
          { value: 'check' },
          { value: 'check-circle' },
          { value: 'exclamation' },
          { value: 'exclamation-circle' },
          { value: 'close' },
          { value: 'close-circle' },
          { value: 'stop' },
          { value: 'warning' },
          { value: 'delete' },
        ],
        default: 'question-circle',
      },
      {
        id: 'centered',
        title: $t('k_180ccf54'),
        type: 'boolean',
        default: false,
      },
    ],
  },
  returnValue: { type: 'boolean', description: $t('k_cac6d06c') },
  execute: async (ctx, params) => {
    const { text: content, okText, cancelText, icon, centered = false, abortIfRejected } = params;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        content: () => <div domPropsInnerHTML={toString(content)}></div>,
        okText: toString(okText),
        cancelText: toString(cancelText),
        icon: toString(icon),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        centered,
      });
    });
    if (!confirmed && abortIfRejected) ctx.abort();
    return confirmed;
  },
};
// 弹出提示
export const messageAtom = {
  id: 'uicore:message',
  name: $t('k_4be19654'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'type',
        title: $t('k_6ef1fc66'),
        type: 'string',
        enum: [
          { value: 'info' },
          { value: 'error' },
          { value: 'success' },
          { value: 'warn' },
          { value: 'loading' },
        ],
        default: 'info',
      },
      {
        id: 'duration',
        title: $t('k_f333bb56'),
        type: 'integer',
        default: 3,
      },
      {
        id: 'isSync',
        title: $t('k_bd1ae025'),
        type: 'boolean',
        default: false,
      },
      {
        id: 'text',
        type: 'string',
        title: $t('k_b0104f9d'),
        default: $t('k_38a16ad0'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
    ],
  },
  execute: async (ctx, params) => {
    const { type = 'info', duration = 3, isSync = false } = params;
    const text = toString(params.text);
    const content = h => h('span', {
      style: {
        display: 'inline-block',
        'white-space': 'pre-wrap',
        'vertical-align': 'top',
        'text-align': 'left',
      },
    }, [text]);
    const promise = message[type](content, duration);

    if (isSync) await promise;
  },
};
// 弹出通知框
export const notificationAtom = {
  id: 'uicore:notification',
  name: $t('k_6d00667a'),
  category: CATEGORY.UI,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'type',
        title: $t('k_6ef1fc66'),
        type: 'string',
        enum: [
          { value: 'open' },
          { value: 'error' },
          { value: 'success' },
          { value: 'warn' },
          { value: 'error' },
        ],
        default: 'open',
      },
      {
        id: 'placement',
        title: $t('k_bea8f01c'),
        type: 'string',
        enum: [
          { value: 'topLeft', label: $t('k_aa249141') },
          { value: 'topRight', label: $t('k_e4aaa5f4') },
          { value: 'bottomLeft', label: $t('k_7a343db') },
          { value: 'bottomRight', label: $t('k_dd02512') },
        ],
        default: 'topRight',
      },
      {
        id: 'duration',
        title: $t('k_f333bb56'),
        type: 'number',
        default: 4.5,
      },
      {
        id: 'message',
        type: 'string',
        title: $t('k_9dcb759a'),
        default: $t('k_9dcb759a'),
      },
      {
        id: 'description',
        type: 'string',
        title: $t('k_d4c387bd'),
        default: $t('k_9e4338a9'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
    ],
  },
  execute: async (ctx, params) => {
    const { type = 'info', placement, message, description, duration, isSync } = params;

    const options = {
      placement,
      message: () => <div domPropsInnerHTML={toString(message)}></div>,
      description: () => <div domPropsInnerHTML={toString(description)}></div>,
      duration,
      isSync,
    };

    notification[type](options);
  },
};

export const promptAtom = {
  id: 'uicore:prompt',
  name: $t('k_c3e01b2f'),
  category: $t('k_9a34c9f8'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'message',
        type: 'string',
        title: $t('k_87d39ce8'),
        default: $t('k_3c44cecd'),
        ui: {
          settings: {
            multipleLine: true,
          },
        },
      },
      {
        id: 'defaultValue',
        type: 'string',
        title: $t('k_af6b478e'),
      },
      // 兼容旧字段, 当现存勾选了 password 时, 外显配置让用户反选后再切换输入框模式
      {
        id: 'password',
        type: 'boolean',
        title: $t('k_edbc9275'),
        condition: '!!params.password',
      },
      {
        id: 'inputType',
        type: 'string',
        title: $t('k_e701eb7'),
        enum: [
          { value: 'input', label: $t('k_1a11e9f0') },
          { value: 'textarea', label: $t('k_6f01c427') },
          { value: 'password', label: $t('k_37e04981') },
        ],
      },
      {
        id: 'allowEmpty',
        type: 'boolean',
        title: $t('k_2b922dc5'),
      },
      {
        id: 'abortIfRejected',
        type: 'boolean',
        title: $t('k_ae19ffdb'),
        default: true,
      },
    ],
  },
  returnValue: { type: 'string', description: $t('k_66fbceac') },
  execute: async (ctx, params) => {
    const { uc } = ctx;
    while (true) {
      // 兼容旧字段 password 优先级最高
      const password = !!params.password || params.inputType === 'password';
      const textarea = params.inputType === 'textarea';

      const answer = await uc?.ui?.prompt({
        message: toString(params.message),
        defaultValue: toString(params.defaultValue),
        password,
        textarea,
      });

      if (answer === null) {
        if (params.abortIfRejected) ctx.abort();
        return params.defaultValue;
      }

      if (answer === '' && !params.allowEmpty) continue;

      return answer;
    }
  },
};

// 打开弹窗
export const showModalAtom = {
  id: 'xy:showModal',
  name: $t('k_b2d9b5a4'),
  category: CATEGORY.UI,
  desc: $t('k_f0f0b2d1'),
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'modalId',
        type: 'string',
        title: $t('k_7635550a'),
        ui: { type: 'XyModalSelector' },
      },
      {
        id: 'globalModalSetting',
        type: 'object',
        title: $t('k_974ae2ed'),
        ui: { type: 'XyGlobalModalSetting' },
        default: {
          keepAlive: false,
          suffix: '',
        },
        condition: 'params.modalId.includes(":")',
      },
      {
        id: 'data',
        type: 'array',
        title: $t('k_31bc6f57'),
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            allowBind: true,
            async getPagelet(inputBox) {
              const { modalId } = inputBox.container.stub.parent.value;
              const isModalPage = modalId?.includes(':');
              if (isModalPage) {
                const [, pageId] = modalId.split(':');
                const { projectId } = inputBox.wContext;
                const { pageConfig: pagelets } = await pageLoader.getPageDetail({
                  projectId,
                  pageId,
                });
                return pagelets?.find(x => x.id === 'default');
              }
              const allModalPagelets = inputBox.wContext.modalList;
              return allModalPagelets.find(x => x.id === modalId);
            },
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string' },
            { id: 'value', type: 'string' },
            { id: 'bind', type: 'string' },
          ],
        },
        'uc:allowExpr': false,
      },
    ],
  },
  paramsFormComponents: [
    XyModalSelectorMeta,
    XyPageletPropsEditorMeta,
    XyGlobalModalSettingMeta,
  ],
  async execute(ctx, params) {
    const { renderer } = ctx;
    const { wContext, w } = renderer;  // xy 上下文
    // [废弃] mergeParams 兼容旧配置
    const { modalId, data, mergeParams, globalModalSetting } = params;
    const dynamicData = {};
    const propsData = {};

    const mixedContext = new Proxy(renderer, {
      get(t, k) {
        if (k === 'flow') return ctx;
        if (k === 'flowState') return ctx.state;
        if (k === '$event') return ctx.$event;

        return Reflect.get(t, k);
      },
    });

    forEach(data, (paramItem) => {
      const { key, value: valueRaw, bind } = paramItem;

      if (bind) {
        // 使用双向绑定的方式结合到当前页面的数据源
        const bindParts = toPathParts(bind);
        Object.defineProperty(dynamicData, key, {
          configurable: false,
          enumerable: true,
          get: () => get(mixedContext, bindParts),
          set: (value) => {
            vueDeepSet(mixedContext, bindParts, value);
          },
        });
      } else {
        // 值传递
        Object.defineProperty(dynamicData, key, {
          configurable: false,
          enumerable: true,
          get: () => {
            if (Object.prototype.hasOwnProperty.call(propsData, key)) return propsData[key];
            return stringInterop(valueRaw, renderer, { flow: ctx, flowState: ctx.state, $event: ctx.$event });
          },
          set: (value) => {
            propsData[key] = value;
          },
        });
      }
    });

    const isModalPage = modalId?.includes(':');
    const [, pageId] = modalId?.split(':');
    const closeRes = isModalPage
      ? await w.showGlobalModal(pageId, dynamicData, globalModalSetting)
      : await wContext.showModal(modalId, dynamicData, { mergeParams, params: data });
    return closeRes;
  },
  returnValue: {
    description: $t('k_72f37eba'),
  },
};

export default [
  alertAtom,
  confirmAtom,
  messageAtom,
  notificationAtom,
  promptAtom,
  showModalAtom,
];
